import { DietType, type Traits, type Vec2, type Obstacle } from './types'
import type { Food } from './food'
import type { SpatialGrid } from './spatial-grid'

let nextId = 0

export const MAX_ENERGY = 250
const BASE_ENERGY = 80

/** 乱数ヘルパー */
function rand(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function randDiet(): DietType {
  const r = Math.random()
  if (r < 0.55) return DietType.Herbivore  // 草食: 55%
  if (r < 0.80) return DietType.Carnivore  // 肉食: 25%
  return DietType.Omnivore                  // 雑食: 20%
}

export function randomTraits(): Traits {
  const diet = randDiet()
  const speedBase = diet === DietType.Carnivore ? 2.5 : diet === DietType.Herbivore ? 1.5 : 2.0
  // 肉食は視野が広い（獲物を探す必要があるため）
  const visionBase = diet === DietType.Carnivore ? rand(80, 160)
    : diet === DietType.Omnivore ? rand(60, 130) : rand(40, 100)
  return {
    speed: rand(speedBase * 0.7, speedBase * 1.3),
    visionRange: visionBase,
    size: rand(
      diet === DietType.Carnivore ? 5 : 3,
      diet === DietType.Carnivore ? 9 : 6,
    ),
    diet,
    hue: diet === DietType.Herbivore ? rand(90, 150) : diet === DietType.Carnivore ? rand(0, 30) : rand(200, 280),
    metabolismRate: rand(0.8, 1.2),
    /** 繁殖力: 高いほど繁殖に必要なエネルギーが低い（雑食は繁殖しにくく調整） */
    fertilityRate: diet === DietType.Omnivore ? rand(0.6, 0.9) : rand(0.8, 1.2),
  }
}

export class Organism {
  id: number
  pos: Vec2
  vel: Vec2
  energy: number
  age: number
  alive: boolean
  traits: Traits
  /** 世代番号（0が初期個体） */
  generation: number
  /** 親個体のID（初期個体はnull） */
  parentId: number | null

  private wanderAngle: number
  private stateTimer: number
  /** 獲物が見つからなかった連続フレーム数 */
  private starvingTicks: number
  /** 捕食クールダウン（キル後に次の捕食まで待機するフレーム数） */
  private huntCooldown: number

  constructor(
    x: number,
    y: number,
    traits?: Traits,
    generation = 0,
    parentId: number | null = null,
  ) {
    this.id = nextId++
    this.pos = { x, y }
    this.vel = { x: 0, y: 0 }
    this.energy = BASE_ENERGY
    this.age = 0
    this.alive = true
    this.traits = traits ?? randomTraits()
    this.wanderAngle = Math.random() * Math.PI * 2
    this.stateTimer = 0
    this.generation = generation
    this.parentId = parentId
    this.starvingTicks = 0
    this.huntCooldown = 0
  }

  update(
    dt: number,
    foods: Food[],
    organisms: Organism[],
    worldW: number,
    worldH: number,
    obstacles: Obstacle[],
    grid?: SpatialGrid,
  ): void {
    this.age += dt

    // 基礎代謝（肉食は高コスト少数精鋭、雑食はジェネラリストとして効率的）
    const dietMultiplier = this.traits.diet === DietType.Carnivore ? 1.5
      : this.traits.diet === DietType.Omnivore ? 0.95 : 0.85
    // サイズコストを非線形化: サイズ5超で急激にコスト増（サイズ暴走を抑制）
    const s = this.traits.size
    const sizeCost = s <= 5 ? s * 0.1 : s * 0.1 + Math.pow(s - 5, 1.5) * 0.02
    const drain = 0.008 * this.traits.metabolismRate * dietMultiplier
      * (1 + this.traits.speed * 0.5 + sizeCost)  // 速度コスト増: 高速個体ほど維持費が高い
    this.energy -= drain * dt * 60

    // 捕食クールダウンの消化
    if (this.huntCooldown > 0) this.huntCooldown--

    // 全個体で飢餓カウンターを進める（食べた時だけリセット）
    this.starvingTicks++

    // 飢餓ペナルティ（全食性に適用 — ドレイン量は基礎代謝の1-3倍程度に抑える）
    if (this.traits.diet === DietType.Carnivore) {
      if (this.starvingTicks > 120) {  // 2秒の猶予
        const escalation = 1 + Math.min((this.starvingTicks - 120) / 180, 2.0)
        this.energy -= 0.03 * escalation * dt * 60  // ~1.8 energy/s（基礎代謝の約1.4倍）
      }
    } else if (this.traits.diet === DietType.Omnivore) {
      if (this.starvingTicks > 150) {
        const escalation = 1 + Math.min((this.starvingTicks - 150) / 200, 1.5)
        this.energy -= 0.025 * escalation * dt * 60  // ~1.5 energy/s
      }
    } else {
      // 草食: 食料枯渇時の個体数調節
      if (this.starvingTicks > 200) {
        const escalation = 1 + Math.min((this.starvingTicks - 200) / 300, 1.5)
        this.energy -= 0.02 * escalation * dt * 60  // ~1.2 energy/s
      }
    }

    if (this.energy <= 0) {
      this.alive = false
      return
    }

    // 逃避行動: 草食個体が近くの捕食者から逃げる（GENAAA-78）
    if (this.traits.diet === DietType.Herbivore) {
      const predator = this.detectPredator(organisms, grid)
      if (predator) {
        const dx = this.pos.x - predator.x
        const dy = this.pos.y - predator.y
        const len = Math.sqrt(dx * dx + dy * dy) || 1
        const spd = this.traits.speed * 60 * 1.3  // 逃避時は1.3倍速
        this.vel.x = (dx / len) * spd
        this.vel.y = (dy / len) * spd
        this.pos.x += this.vel.x * dt
        this.pos.y += this.vel.y * dt
        this.resolveObstacles(obstacles)
        this.pos.x = ((this.pos.x % worldW) + worldW) % worldW
        this.pos.y = ((this.pos.y % worldH) + worldH) % worldH
        this.eatFood(foods)
        if (this.energy > MAX_ENERGY) this.energy = MAX_ENERGY
        this.stateTimer += dt
        return
      }
    }

    // 最も近い食料を探す
    const target = this.findTarget(foods, organisms, grid)

    if (target) {
      this.moveToward(target, dt)
    } else {
      this.wander(dt)
    }

    // 障害物との衝突処理
    this.resolveObstacles(obstacles)

    // 境界折り返し
    this.pos.x = ((this.pos.x % worldW) + worldW) % worldW
    this.pos.y = ((this.pos.y % worldH) + worldH) % worldH

    // 食事判定
    this.eat(foods, organisms)

    // エネルギー上限
    if (this.energy > MAX_ENERGY) this.energy = MAX_ENERGY

    this.stateTimer += dt
  }

  /** 近くの捕食者を検出（逃避行動用） */
  private detectPredator(organisms: Organism[], grid?: SpatialGrid): Vec2 | null {
    const vr = this.traits.visionRange * 1.1  // やや広めの検知で早期逃避
    let closest: Vec2 | null = null
    let closestDist = vr

    const candidates = grid
      ? grid.queryRadius(this.pos, vr, this.id)
      : organisms

    for (const o of candidates) {
      if (!o.alive || o.id === this.id) continue
      if (o.traits.diet !== DietType.Carnivore && o.traits.diet !== DietType.Omnivore) continue
      const d = dist(this.pos, o.pos)
      if (d < closestDist) {
        closestDist = d
        closest = o.pos
      }
    }
    return closest
  }

  /** 草食的食事のみ（逃避中に使用） */
  private eatFood(foods: Food[]): void {
    for (const f of foods) {
      if (!f.alive) continue
      if (dist(this.pos, f.pos) < this.traits.size + f.radius) {
        this.energy += f.energy
        this.starvingTicks = 0
        f.alive = false
      }
    }
  }

  private findTarget(foods: Food[], organisms: Organism[], grid?: SpatialGrid): Vec2 | null {
    const { visionRange, diet } = this.traits
    let best: Vec2 | null = null
    let bestDist = visionRange

    // 食料探索（Food には SpatialGrid を使わない。個体数に比べて少なく効果が薄い）
    if (diet === DietType.Herbivore || diet === DietType.Omnivore) {
      for (const f of foods) {
        if (!f.alive) continue
        const d = dist(this.pos, f.pos)
        if (d < bestDist) {
          bestDist = d
          best = f.pos
        }
      }
    }

    if (diet === DietType.Carnivore || diet === DietType.Omnivore) {
      const candidates = grid
        ? grid.queryRadius(this.pos, visionRange, this.id)
        : organisms

      for (const o of candidates) {
        if (!o.alive || o.id === this.id) continue
        let isValidPrey: boolean
        if (diet === DietType.Carnivore) {
          isValidPrey = o.traits.diet === DietType.Herbivore
            || (o.traits.diet === DietType.Omnivore && this.traits.size > o.traits.size * 0.55)
        } else {
          // 雑食: 草食は自分の90%未満のサイズなら狩れる、それ以外は60%未満
          isValidPrey = o.traits.diet === DietType.Herbivore
            ? o.traits.size < this.traits.size * 0.9
            : o.traits.size < this.traits.size * 0.6
        }
        if (!isValidPrey) continue
        const d = dist(this.pos, o.pos)
        if (d < bestDist) {
          bestDist = d
          best = o.pos
        }
      }
    }

    return best
  }

  private moveToward(target: Vec2, dt: number): void {
    const dx = target.x - this.pos.x
    const dy = target.y - this.pos.y
    const len = Math.sqrt(dx * dx + dy * dy) || 1
    const spd = this.traits.speed * 60
    this.vel.x = (dx / len) * spd
    this.vel.y = (dy / len) * spd
    this.pos.x += this.vel.x * dt
    this.pos.y += this.vel.y * dt
  }

  private wander(dt: number): void {
    this.wanderAngle += (Math.random() - 0.5) * 0.3
    const spd = this.traits.speed * 40
    this.vel.x = Math.cos(this.wanderAngle) * spd
    this.vel.y = Math.sin(this.wanderAngle) * spd
    this.pos.x += this.vel.x * dt
    this.pos.y += this.vel.y * dt
  }

  private resolveObstacles(obstacles: Obstacle[]): void {
    for (const obs of obstacles) {
      const dx = this.pos.x - obs.pos.x
      const dy = this.pos.y - obs.pos.y
      const d = Math.sqrt(dx * dx + dy * dy)
      const minDist = obs.radius + this.traits.size
      if (d < minDist && d > 0) {
        const nx = dx / d
        const ny = dy / d
        this.pos.x = obs.pos.x + nx * minDist
        this.pos.y = obs.pos.y + ny * minDist
        const dot = this.vel.x * nx + this.vel.y * ny
        this.vel.x -= 2 * dot * nx
        this.vel.y -= 2 * dot * ny
      }
    }
  }

  private eat(foods: Food[], organisms: Organism[]): void {
    const { diet, size } = this.traits

    if (diet === DietType.Herbivore || diet === DietType.Omnivore) {
      for (const f of foods) {
        if (!f.alive) continue
        if (dist(this.pos, f.pos) < size + f.radius) {
          this.energy += f.energy
          this.starvingTicks = 0
          f.alive = false
        }
      }
    }

    if (diet === DietType.Carnivore || diet === DietType.Omnivore) {
      if (this.huntCooldown > 0) return  // クールダウン中は捕食不可
      for (const o of organisms) {
        if (!o.alive || o.id === this.id) continue
        let isValidPrey: boolean
        if (diet === DietType.Carnivore) {
          isValidPrey = o.traits.diet === DietType.Herbivore
            || (o.traits.diet === DietType.Omnivore && size > o.traits.size * 0.55)
        } else {
          // 雑食: 草食は自分の90%未満のサイズなら狩れる、それ以外は60%未満
          isValidPrey = o.traits.diet === DietType.Herbivore
            ? o.traits.size < size * 0.9
            : o.traits.size < size * 0.6
        }
        if (!isValidPrey) continue
        if (dist(this.pos, o.pos) < size + o.traits.size) {
          // 回避判定: 獲物の速度比で回避確率が上がる
          const speedRatio = o.traits.speed / this.traits.speed
          const dodgeChance = Math.min(speedRatio * 0.25, 0.40)
          if (Math.random() < dodgeChance) continue  // 獲物が回避成功

          const predationRatio = diet === DietType.Omnivore ? 0.25
            : (o.traits.diet === DietType.Omnivore ? 0.22 : 0.32)
          this.energy += o.energy * predationRatio
          this.starvingTicks = 0
          o.alive = false
          this.huntCooldown = diet === DietType.Carnivore ? 30 : 20  // キル後のクールダウン
          break  // 1フレーム1キルまで
        }
      }
    }
  }
}

function dist(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}
